// frontend/src/Components/SyncTo/RecordingProvider.jsx
// Phase 30 — React context placed above the HashRouter in App.js.
// (Invariant #6 / PITFALLS #21.)
//
// OWNS:
//   - useSyncToRecorder() state (delegated)
//   - Modal open/close (D-01 / D-03)
//   - Orphan prompt state (D-08) — userId-scoped, 24h TTL (via scanOrphans)
//   - SYNC-04 sub-dialogs (mic-denied, first-silence, rolling-warn, zero-byte)
//   - beforeunload handler while state !== 'idle' (PITFALLS #10)
//   - D-05 auto-stop toast (via NotificationContext — WHY this provider nests
//     INSIDE NotificationContext.Provider in App.js, not outside)
//
// PLACEMENT: App.js — INSIDE NotificationContext.Provider,
// OUTSIDE the HashRouter element.
//
// Resume semantics on orphan = DOWNLOAD (Phase 30 default).
// MediaRecorder state is gone after tab close; true "continue recording"
// is a Phase 34 extension.
//
// This file deliberately imports nothing from react-router-dom and renders
// no router elements — it is router-agnostic by design.

import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useNotification } from '../../NotificationContext';
import { useSyncToRecorder } from './hooks/useSyncToRecorder';
import { scanOrphans, assembleBlob, purgeBySessionKey } from './hooks/useSyncToIDB';
import { readUserIdFromJwt } from './helpers/userIdFromJwt';
import { downloadBlob, buildDownloadFilename } from './helpers/downloadBlob';
import ReviewModal from './ReviewModal';
import FailureModal from './FailureModal';
import { useSyncToApi } from './hooks/useSyncToApi';

export const RecordingContext = createContext(null);
export function useRecording() { return useContext(RecordingContext); }

const ORPHAN_DISMISS_KEY = 'syncto.orphans.dismissed';

export default function RecordingProvider({ children }) {
  const notification = useNotification();
  const addNotification = notification?.addNotification;

  const [modalOpen, setModalOpen] = useState(false);
  const [meta, setMeta] = useState(null);

  // Phase 32 — Review-then-apply state.
  const [reviewOpen,       setReviewOpen]       = useState(false);
  const [reviewCleaned,    setReviewCleaned]    = useState('');
  const [reviewFailed,     setReviewFailed]     = useState(false);
  const [reviewSessionId,  setReviewSessionId]  = useState(null);
  const [reviewTranscript, setReviewTranscript] = useState('');
  const [reviewDurationMs, setReviewDurationMs] = useState(0);
  const [transcribing,     setTranscribing]     = useState(false);
  // Phase 33 — Template-variant review state. Threaded from meta (Session.jsx
  // passes templatePages + existingCapturedValues to openModal) on openModal,
  // then populated with routedValues + dropped from the /transcribe Template-
  // branch response inside onStopped.
  const [reviewRoutedValues,     setReviewRoutedValues]     = useState({});
  const [reviewDropped,          setReviewDropped]          = useState([]);
  const [reviewExistingCaptured, setReviewExistingCaptured] = useState({});
  const [reviewTemplateId,       setReviewTemplateId]       = useState(null);
  const [reviewTemplatePages,    setReviewTemplatePages]    = useState([]);
  const [reviewRoutingFailed,    setReviewRoutingFailed]    = useState(false);
  // Tracks the IDB sessionKey for the in-flight review so we can purge chunks
  // after apply-success or review-close. useSyncToRecorder.stop() no longer
  // purges when onStopped is set — the caller (us) owns the chunks so
  // FailureModal can re-assemble for download.
  const [reviewSessionKey,       setReviewSessionKey]       = useState(null);

  // Phase 34 — Failure fallback state. Populated when transcribe() returns
  // error.attempts >= 3 (exhausted) OR status === 504 (timeout → timeoutMode).
  const [failureOpen,        setFailureOpen]        = useState(false);
  const [failureAttempts,    setFailureAttempts]    = useState(0);
  const [failureMessage,     setFailureMessage]     = useState('');
  const [failureSessionKey,  setFailureSessionKey]  = useState(null);
  const [failureMimeType,    setFailureMimeType]    = useState('');
  const [failureTimeoutMode, setFailureTimeoutMode] = useState(false);
  // Ref synced to recorder.sessionKey below (recorder declared AFTER
  // onStopped — ref lets onStopped read the current sessionKey at call-time
  // without re-ordering the declarations).
  const sessionKeyRef = useRef(null);

  const { transcribe } = useSyncToApi();

  const [micDeniedOpen,       setMicDeniedOpen]       = useState(false);
  const [firstSilenceOpen,    setFirstSilenceOpen]    = useState(false);
  const [rollingSilenceOpen,  setRollingSilenceOpen]  = useState(false);
  const [zeroByteFailureOpen, setZeroByteFailureOpen] = useState(false);

  const [orphans, setOrphans] = useState([]);
  const [orphanDismissed, setOrphanDismissed] = useState(false);

  const onStopped = useCallback(async (blob) => {
    // Close the recording modal immediately.
    setModalOpen(false);
    // D-05 / D-05a — assembled Blob → /transcribe → ReviewModal.
    if (!blob || blob.size === 0) return;
    // Snapshot the sessionKey BEFORE awaiting transcribe(). useSyncToRecorder.stop()
    // has already queued setSessionKey(null); during the await, RecordingProvider
    // re-renders and the useEffect at line ~170 resets sessionKeyRef.current to
    // null. Reading the ref post-await would give us null → reviewSessionKey
    // becomes null → purgeBySessionKey is skipped on Apply-success → IDB orphan.
    const capturedSessionKey = sessionKeyRef.current;
    setTranscribing(true);
    const mode = (meta && meta.mode) || 'note';
    const sessionId = meta && meta.sessionId;
    const templateId = meta && meta.templateId;
    const mimeType = blob.type;
    const { data, error: err } = await transcribe({ blob, mode, mimeType, templateId });
    setTranscribing(false);
    if (err || !data) {
      // Phase 34 — exhausted backoff OR timeout → FailureModal.
      setFailureAttempts(typeof err?.attempts === 'number' ? err.attempts : 3);
      setFailureMessage((err && err.message) || 'Unknown error');
      setFailureSessionKey(capturedSessionKey);
      setFailureMimeType(mimeType || (blob && blob.type) || '');
      setFailureTimeoutMode(err?.status === 504);
      setFailureOpen(true);
      return;
    }
    setReviewCleaned(data.cleaned || '');
    setReviewFailed(!!data.cleanupFailed);
    setReviewSessionId(sessionId || null);
    setReviewSessionKey(capturedSessionKey);
    setReviewTranscript(typeof data.transcript === 'string' ? data.transcript : '');
    setReviewDurationMs(typeof data.durationMs === 'number' ? data.durationMs : 0);
    // Phase 33 — Template-variant review state. Branch on data.mode so Note-
    // mode runs clear the template state (avoids stale rows if the user
    // switches modes between consecutive recordings). The meta object carries
    // templateId + templatePages + existingCapturedValues which Session.jsx
    // supplies when mode === 'template' (ModeModal lands in Phase 34; for
    // now SyncToButton keeps defaulting to 'note').
    // Couple data.mode to meta.mode — if the server's echo disagrees with the
    // client's captured intent (fuzzed/buggy response), fall through to the
    // Note branch and clear template state so ReviewModal renders cleanly
    // without a useless empty-rows Template variant (WR-04).
    const isTemplateMode = data.mode === 'template' && meta?.mode === 'template';
    if (isTemplateMode) {
      setReviewRoutedValues(data.routedValues || {});
      setReviewDropped(Array.isArray(data.dropped) ? data.dropped : []);
      setReviewTemplateId((meta && meta.templateId) || null);
      setReviewTemplatePages((meta && Array.isArray(meta.templatePages)) ? meta.templatePages : []);
      setReviewExistingCaptured((meta && meta.existingCapturedValues) || {});
    } else {
      setReviewRoutedValues({});
      setReviewDropped([]);
      setReviewTemplateId(null);
      setReviewTemplatePages([]);
      setReviewExistingCaptured({});
    }
    setReviewOpen(true);
  }, [meta, transcribe]);

  const recorder = useSyncToRecorder({
    onMicDenied:          useCallback(() => setMicDeniedOpen(true), []),
    onZeroByteFailure:    useCallback(() => setZeroByteFailureOpen(true), []),
    onFirstSilence:       useCallback(() => setFirstSilenceOpen(true), []),
    onRollingSilenceWarn: useCallback(() => setRollingSilenceOpen(true), []),
    onAutoStop: useCallback(() => {
      addNotification?.({
        type: 'info',
        message: 'Auto-stopped after 60 seconds of silence — your recording was downloaded.',
      });
    }, [addNotification]),
    onStopped,
    onCancelled: useCallback(() => { setModalOpen(false); }, []),
    downloadOnStop: false,
  });

  // Phase 34 — keep the ref in sync with the live recorder.sessionKey so the
  // onStopped useCallback (declared BEFORE useSyncToRecorder above) can read
  // the current sessionKey at call-time when pushing into failure state.
  useEffect(() => {
    sessionKeyRef.current = recorder?.sessionKey || null;
  }, [recorder?.sessionKey]);

  const openModal = useCallback((m) => {
    setMeta(m || null);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => { setModalOpen(false); }, []);

  const currentUserIdRef = useRef(null);
  useEffect(() => {
    let cancelled = false;

    async function runScan(userId) {
      if (cancelled) return;
      currentUserIdRef.current = userId;
      const dismissed = sessionStorage.getItem(ORPHAN_DISMISS_KEY);
      if (dismissed === userId) { setOrphanDismissed(true); return; }
      try {
        const rows = await scanOrphans(userId);
        if (cancelled) return;
        const filtered = (rows || []).filter((row) => row.userId === userId);
        setOrphans(filtered);
        setOrphanDismissed(false);
      } catch {
        /* IDB open failure — silently skip */
      }
    }

    const initialUid = readUserIdFromJwt();
    if (initialUid) {
      runScan(initialUid);
      return () => { cancelled = true; };
    }

    const interval = setInterval(() => {
      if (cancelled) { clearInterval(interval); return; }
      const uid = readUserIdFromJwt();
      if (uid) {
        clearInterval(interval);
        runScan(uid);
      }
    }, 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const dismissOrphanPrompt = useCallback(() => {
    setOrphanDismissed(true);
    const uid = currentUserIdRef.current;
    if (uid) { try { sessionStorage.setItem(ORPHAN_DISMISS_KEY, uid); } catch {} }
  }, []);

  const downloadOrphan = useCallback(async (sessionKey) => {
    const row = orphans.find((o) => o.sessionKey === sessionKey);
    if (!row) return;
    try {
      const blob = await assembleBlob(sessionKey, row.mimeType);
      if (blob && blob.size > 0) {
        downloadBlob(blob, buildDownloadFilename(sessionKey, row.mimeType));
      }
    } finally {
      await purgeBySessionKey(sessionKey);
      setOrphans((prev) => prev.filter((o) => o.sessionKey !== sessionKey));
    }
  }, [orphans]);

  const discardOrphan = useCallback(async (sessionKey) => {
    try { await purgeBySessionKey(sessionKey); } catch {}
    setOrphans((prev) => prev.filter((o) => o.sessionKey !== sessionKey));
  }, []);

  // Phase 30 default: Resume aliases to Download. MediaRecorder state is gone
  // after tab close; true "continue recording" is a Phase 34 extension.
  const resumeOrphan = useCallback(async (sessionKey) => {
    await downloadOrphan(sessionKey);
  }, [downloadOrphan]);

  useEffect(() => {
    if (recorder.state === 'idle') return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [recorder.state]);

  const dismissMicDenied       = useCallback(() => setMicDeniedOpen(false),       []);
  const dismissFirstSilence    = useCallback(() => setFirstSilenceOpen(false),    []);
  const dismissRollingSilence  = useCallback(() => setRollingSilenceOpen(false),  []);
  const dismissZeroByteFailure = useCallback(() => setZeroByteFailureOpen(false), []);

  // Phase 34 — FailureModal handlers.
  const dismissFailure = useCallback(() => {
    setFailureOpen(false);
    setFailureAttempts(0);
    setFailureMessage('');
    setFailureSessionKey(null);
    setFailureMimeType('');
    setFailureTimeoutMode(false);
  }, []);

  // Phase 34 SC7 — re-fetch /api/sync-to/user-feature-flag when ReviewModal
  // surfaces a 403 mid-Apply. The SyncToButton render gate reads the JWT
  // (sessionStorage 'user' → decoded.features.syncTo); we cannot refresh the
  // JWT without a full re-login, so we PERSIST a fresh { enabled, checkedAt }
  // signal to a new sessionStorage key ('syncto.flag.fresh') as an
  // opportunistic freshness hint. The backend is the authority — this key
  // cannot grant access; it only disables UX surfaces earlier. IDB chunks
  // are NOT purged here (PITFALLS #11 — user can still download via
  // FailureModal on a subsequent session if orphan-scan surfaces them).
  const refreshFeatureFlag = useCallback(async () => {
    try {
      const URL_BASE =
        process.env.REACT_APP_API_BASE_PROD_URL ||
        process.env.REACT_APP_API_BASE_URL ||
        'http://localhost:3002';
      const token = sessionStorage.getItem('user');
      const resp = await fetch(URL_BASE + '/api/sync-to/user-feature-flag', {
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      });
      if (!resp.ok) return;
      const json = await resp.json();
      try {
        sessionStorage.setItem('syncto.flag.fresh', JSON.stringify({
          enabled: !!(json && json.enabled),
          checkedAt: Date.now(),
        }));
      } catch { /* storage quota — ignore */ }
    } catch { /* network — ignore, fail-closed on next re-check */ }
  }, []);

  const retryFailure = useCallback(async (blob) => {
    if (!blob) return;
    // Snapshot sessionKey pre-await — see onStopped for the rationale.
    const capturedSessionKey = sessionKeyRef.current;
    setFailureOpen(false);
    setTranscribing(true);
    const mode = (meta && meta.mode) || 'note';
    const sessionId = meta && meta.sessionId;
    const templateId = meta && meta.templateId;
    const mimeType = blob.type;
    const { data, error: err } = await transcribe({ blob, mode, mimeType, templateId });
    setTranscribing(false);
    if (err || !data) {
      setFailureAttempts(typeof err?.attempts === 'number' ? err.attempts : 3);
      setFailureMessage((err && err.message) || 'Unknown error');
      setFailureSessionKey(capturedSessionKey);
      setFailureMimeType(mimeType || '');
      setFailureTimeoutMode(err?.status === 504);
      setFailureOpen(true);
      return;
    }
    // Success on retry — populate review state exactly like the happy-path onStopped.
    setReviewCleaned(data.cleaned || '');
    setReviewFailed(!!data.cleanupFailed);
    setReviewSessionId(sessionId || null);
    setReviewSessionKey(capturedSessionKey);
    setReviewTranscript(typeof data.transcript === 'string' ? data.transcript : '');
    setReviewDurationMs(typeof data.durationMs === 'number' ? data.durationMs : 0);
    const isTemplateMode = data.mode === 'template' && meta?.mode === 'template';
    if (isTemplateMode) {
      setReviewRoutedValues(data.routedValues || {});
      setReviewDropped(Array.isArray(data.dropped) ? data.dropped : []);
      setReviewTemplateId((meta && meta.templateId) || null);
      setReviewTemplatePages((meta && Array.isArray(meta.templatePages)) ? meta.templatePages : []);
      setReviewExistingCaptured((meta && meta.existingCapturedValues) || {});
    } else {
      setReviewRoutedValues({});
      setReviewDropped([]);
      setReviewTemplateId(null);
      setReviewTemplatePages([]);
      setReviewExistingCaptured({});
    }
    setReviewOpen(true);
  }, [meta, transcribe]);

  const value = useMemo(() => ({
    state: recorder.state,
    timer: recorder.timer,
    level: recorder.level,
    sessionKey: recorder.sessionKey,
    start:  recorder.start,
    pause:  recorder.pause,
    resume: recorder.resume,
    stop:   recorder.stop,
    cancel: recorder.cancel,
    modalOpen, meta, openModal, closeModal,
    micDeniedOpen,       dismissMicDenied,
    firstSilenceOpen,    dismissFirstSilence,
    rollingSilenceOpen,  dismissRollingSilence,
    zeroByteFailureOpen, dismissZeroByteFailure,
    orphans: orphanDismissed ? [] : orphans,
    dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  }), [
    recorder.state, recorder.timer, recorder.level, recorder.sessionKey,
    recorder.start, recorder.pause, recorder.resume, recorder.stop, recorder.cancel,
    modalOpen, meta, openModal, closeModal,
    micDeniedOpen, dismissMicDenied,
    firstSilenceOpen, dismissFirstSilence,
    rollingSilenceOpen, dismissRollingSilence,
    zeroByteFailureOpen, dismissZeroByteFailure,
    orphans, orphanDismissed, dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  ]);

  return (
    <RecordingContext.Provider value={value}>
      {children}
      <ReviewModal
        show={reviewOpen}
        onClose={() => {
          setReviewOpen(false);
          // Chunks are no longer needed — user dismissed after reviewing.
          // (useSyncToRecorder.stop() no longer purges when onStopped is set.)
          if (reviewSessionKey) {
            purgeBySessionKey(reviewSessionKey).catch(() => {});
            setReviewSessionKey(null);
          }
        }}
        onApplied={(result) => {
          // Phase 34 SC7 — 403 feature-flag-disabled path. Keep ReviewModal
          // OPEN so the user sees the disabled banner; do NOT forward the
          // result to meta.onApplied (Session.jsx should not switch tabs on a
          // failed apply). Re-fetch /user-feature-flag to persist a fresh
          // signal and leave IDB chunks intact (PITFALLS #11).
          if (result && result.featureDisabled) {
            refreshFeatureFlag();
            return;
          }
          setReviewOpen(false);
          // Successful apply — audio has served its purpose; purge IDB chunks.
          if (reviewSessionKey) {
            purgeBySessionKey(reviewSessionKey).catch(() => {});
            setReviewSessionKey(null);
          }
          if (meta && typeof meta.onApplied === 'function') {
            meta.onApplied(result);
          }
        }}
        // Phase 33 — mode is driven by meta (Session.jsx sets 'template' when
        // ModeModal is wired in Phase 34; currently defaults to 'note'). The
        // ReviewModal branches its render on this value.
        mode={(meta && meta.mode) || 'note'}
        sessionId={reviewSessionId}
        cleaned={reviewCleaned}
        cleanupFailed={reviewFailed}
        transcript={reviewTranscript}
        durationMs={reviewDurationMs}
        // Phase 33 — Template-variant props (undefined / empty for note mode).
        templateId={reviewTemplateId}
        templatePages={reviewTemplatePages}
        routedValues={reviewRoutedValues}
        dropped={reviewDropped}
        existingCapturedValues={reviewExistingCaptured}
      />
      <Modal
        show={transcribing}
        backdrop="static"
        keyboard={false}
        centered
        size="sm"
      >
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" role="status" className="mb-3" />
          <div className="small text-muted">
            Transcribing and analysing — this can take up to a minute.
          </div>
        </Modal.Body>
      </Modal>
      <FailureModal
        show={failureOpen}
        onClose={dismissFailure}
        sessionKey={failureSessionKey}
        mimeType={failureMimeType}
        attempts={failureAttempts}
        errorMessage={failureMessage}
        timeoutMode={failureTimeoutMode}
        onRetry={retryFailure}
        onDismiss={dismissFailure}
      />
    </RecordingContext.Provider>
  );
}
